import useInnovaAxios from './useInnovaAxios'
import { useCallback, useMemo } from 'react'
import { unescapeHtml } from 'utils/htmlSymbolHandling'
import { useAuth0 } from '@auth0/auth0-react'
import useInnovaAuth from './useInnovaAuth'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '../queryKeys'

const useObjectsOfInterest = () => {
  const { isAuthenticated } = useAuth0()
  const { user } = useInnovaAuth()
  const queryClient = useQueryClient()
  const userName = user?.name || ''
  const key = useMemo(() => [QUERY_KEYS.objectsOfInterestKey, userName], [userName])

  const fetchDropDowns = useInnovaAxios({
    url: '/user/getObjectsOfInterestDropDowns',
  })

  const fetchObjectsOfInterest = useInnovaAxios({
    url: '/user/getObjectsOfInterest',
  })

  const updateObjectsOfInterest = useInnovaAxios({
    url: '/user/setObjectsOfInterest',
  })

  const { data: objectsOfInterest, refetch: refetchObjectsOfInterest } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await fetchObjectsOfInterest()
      if (res?.error || !res?.data) {
        throw new Error('Failed to fetch objects of interest')
      }
      return res.data
    },
    staleTime: Infinity,
    enabled: isAuthenticated && !!userName,
  })

  const { data: dropDowns } = useQuery({
    queryKey: [QUERY_KEYS.objectsOfInterestDropDownsKey, userName],
    queryFn: async () => {
      const res = await fetchDropDowns()
      if (res?.error || !res?.data) {
        throw new Error('Failed to fetch objects of interest dropdowns')
      }
      const dd = {}
      for (const key in res.data) {
        dd[key] = Array.isArray(res.data[key])
          ? res.data[key].map((val) =>
              typeof val === 'object' ? val : { label: unescapeHtml(val), value: unescapeHtml(val) },
            )
          : []
      }
      return dd
    },
    staleTime: Infinity,
  })

  const { mutateAsync: updateUserObjectsOfInterest } = useMutation({
    mutationFn: async (newObjects) => {
      if (
        !newObjects ||
        !newObjects.rigs ||
        !newObjects.districts ||
        !newObjects.operators ||
        !Array.isArray(newObjects.rigs) ||
        !Array.isArray(newObjects.districts) ||
        !Array.isArray(newObjects.operators)
      )
        return

      const payload = {
        rigs: newObjects.rigs.join('|'),
        operators: newObjects.operators.join('|'),
        districts: newObjects.districts.join('|'),
      }

      const res = await updateObjectsOfInterest(payload)

      return res
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })

  const hasObjectsOfInterest = useCallback(() => {
    if (!objectsOfInterest) return false
    if (
      objectsOfInterest.hasOwnProperty('districts') &&
      Array.isArray(objectsOfInterest.districts) &&
      objectsOfInterest.districts.length > 0
    ) {
      return true
    }

    if (
      objectsOfInterest.hasOwnProperty('rigs') &&
      Array.isArray(objectsOfInterest.rigs) &&
      objectsOfInterest.rigs.length > 0
    ) {
      return true
    }

    if (
      objectsOfInterest.hasOwnProperty('operators') &&
      Array.isArray(objectsOfInterest.operators) &&
      objectsOfInterest.operators.length > 0
    ) {
      return true
    }

    return false
  }, [objectsOfInterest])

  const getObjectValues = useCallback((param) => {
    if (!param) return []
    if (typeof param !== 'string') return []
    if (param === '') return []
    if (!objectsOfInterest) return []
    if (!objectsOfInterest.hasOwnProperty(param)) return []
    if (!Array.isArray(objectsOfInterest[param])) return []
    return objectsOfInterest[param]
  }, [objectsOfInterest])

  const isObjectOfInterest = useCallback((rig, operator, district) => {
    if (!hasObjectsOfInterest()) return false
    if (typeof operator !== 'string') operator = ''
    if (typeof rig !== 'string') rig = ''
    if (typeof district !== 'string') district = ''
    if (operator === '' && rig === '' && district === '') return false

    let operators = getObjectValues('operators')
    if (operators.findIndex((op) => op === operator) >= 0) return true

    let rigs = getObjectValues('rigs')
    if (rigs.findIndex((r) => r === rig) >= 0) return true

    let districts = getObjectValues('districts')
    if (districts.findIndex((d) => d === district) >= 0) return true
    return false
  }, [getObjectValues, hasObjectsOfInterest])

  const refreshObjectsOfInterest = useCallback(() => refetchObjectsOfInterest(), [refetchObjectsOfInterest])

  const toggleRigFavorite = useCallback(
    async (rig) => {
      if (!rig) return

      const prev = queryClient.getQueryData(key) || {}
      const prevRigs = Array.isArray(prev?.rigs) ? prev.rigs : []
      const prevOps = Array.isArray(prev?.operators) ? prev.operators : []
      const prevDists = Array.isArray(prev?.districts) ? prev.districts : []

      const nextRigs = prevRigs.includes(rig) ? prevRigs.filter((r) => r !== rig) : [...prevRigs, rig]

      queryClient.setQueryData(key, { ...prev, rigs: nextRigs })

      try {
        await updateUserObjectsOfInterest({
          rigs: nextRigs,
          operators: prevOps,
          districts: prevDists,
        })
      } catch (e) {
        queryClient.setQueryData(key, prev)
        throw e
      }
    },
    [key, queryClient, updateUserObjectsOfInterest],
  )

  return useMemo(
    () => ({
      dropDowns,
      objectsOfInterest,
      updateUserObjectsOfInterest,
      refreshObjectsOfInterest,
      hasObjectsOfInterest,
      getObjectValues,
      isObjectOfInterest,
      toggleRigFavorite,
    }),
    [
      dropDowns,
      objectsOfInterest,
      updateUserObjectsOfInterest,
      refreshObjectsOfInterest,
      hasObjectsOfInterest,
      getObjectValues,
      isObjectOfInterest,
      toggleRigFavorite,
    ],
  )
}

export default useObjectsOfInterest
